import React from 'react'
import clsx from 'clsx'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import {
  Typography,
  Box,
  Checkbox,
  Fade,
  Tooltip,
  withStyles,
} from '@material-ui/core'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import Price from '../Shop/Price'
import Button from '../Button/Button'
import Loader from '../Loader/Loader'
import DownloadLink from '../Global/DownloadLink'
import DownloadInvoiceButton from '../Order/DownloadInvoiceButton/DownloadInvoiceButton'
import { isSubscription } from '@/utils/constants'
import { useTakeback } from '@/hooks/useTakeback'
import ReturningReasonForm from '../Order/ReturningReasonForm'
import CMSText from '../Global/CMSText'
import { useTranslation } from 'react-i18next'
import { downloadURI } from '../../utils/utils'

const MainTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontSize: 12,
    textAlign: 'center',
    padding: theme.spacing(1),
    width: '100%',
  },
  arrow: {
    color: theme.palette.secondary.main,
  },
}))(Tooltip)

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    hide: {
      opacity: 0,
      transition: theme.transitions.create('opacity'),
    },
    show: {
      opacity: 1,
      transition: theme.transitions.create('opacity'),
    },
    takeBackListTitle: {
      marginBottom: theme.spacing(2),
    },
    takeBackListHeader: {
      display: 'flex',
      justifyContent: 'start',
      alignItems: 'center',
    },
    takeBackItemTitle: {
      paddingLeft: theme.spacing(1),
    },
    circle: {
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
    productCard: {
      display: 'flex',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 60,
      background: theme.palette.common.white,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    productCardCheckbox: {},
    productCardImg: {
      width: 50,
      height: 50,
      position: 'relative',
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginRight: theme.spacing(1),
      },
    },
    productCardContent: {
      flex: 1,
    },
    takeBackItem: {
      borderLeft: `1px solid currentColor`,
      paddingLeft: theme.spacing(2),
      marginLeft: theme.spacing(1),
      paddingBottom: theme.spacing(3),
    },
    takeBackItemActive: {
      borderColor: 'transparent',
      paddingLeft: 0,
      marginLeft: theme.spacing(1),
    },
    takeBackItemLast: {
      borderLeft: 0,
    },
    imgProduct: {
      width: 50,
      height: 50,
      objectFit: 'contain',
      display: 'block',
    },
    productName: {},
    productStatus: {
      marginLeft: theme.spacing(2),
      [theme.breakpoints.down('md')]: {
        marginLeft: theme.spacing(1),
      },
    },
    takeBackOrder: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      width: '100%',
    },
    orderDetailsRow: {
      textAlign: 'right',
    },
    orderDetailsBtn: {},
    orderDetailsRowActions: {
      padding: theme.spacing(1, 0, 2),
    },
    simpleButton: {
      padding: 0,
      textTransform: 'inherit',
      color: theme.extraColors.greyText,
      '&:hover, &:focus': {
        background: 'none',
      },
      '& .MuiSvgIcon-root': {
        fontSize: '.8rem',
      },
    },
    simpleButtonPrimary: {
      color: theme.palette.primary.main,
    },
    simpleButtonDanger: {
      color: theme.palette.error.main,
    },
    currentOrderList: {
      padding: theme.spacing(3),
      background: theme.palette.common.white,
    },
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '20vh',
    },
    returnCtaContainer: {
      minHeight: '2rem',
    },
    downloadInvoiceButton: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(1.5),
    },
  })
)

type Tprops = {
  takeback: any
  main: any
  account: any
  order: any
  activeOrder: boolean
  isModifying: boolean
  isDisabled: boolean
  isDownloadLoading: boolean
  downloadLink: string
  productSelection: any
  productToReturn: any
  productReturningReasons: any
  isOrderReturnable: any
  orderDate: string
  handleProductSelection: any
  handleReturningReasonChange: any
  handleReturningReasonCommentChange: any
  handleActiveCurrentOrder: any
  lastOrder: boolean
  warehouseId: string
  handleDownloadInvoice: any
  invoiceDownloadError: boolean
  tooltipWithdrawalText: any
  setIsDownloadLoading: (val: boolean) => void
  setDownloadLink: (val: string) => void
  user: any
  postProductToReturn: (data: object) => Promise<any>
  index: number
}
const TakeBackOrderItem = ({
  takeback,
  main,
  account,
  order,
  activeOrder,
  isModifying,
  isDisabled,
  isDownloadLoading,
  downloadLink,
  productSelection,
  productToReturn,
  productReturningReasons,
  isOrderReturnable,
  orderDate,
  handleProductSelection,
  handleReturningReasonChange,
  handleReturningReasonCommentChange,
  handleActiveCurrentOrder,
  lastOrder,
  warehouseId,
  handleDownloadInvoice,
  invoiceDownloadError,
  tooltipWithdrawalText,
  setIsDownloadLoading,
  setDownloadLink,
  user,
  postProductToReturn,
  index,
}: Tprops) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { productCheckState } = useTakeback()
  const states = productCheckState(takeback?.data)

  const sendProductsToReturn = async () => {
    setIsDownloadLoading(true)
    const productData = []
    productToReturn.map((product) => {
      const returningReason = productReturningReasons[product.id] || {}
      productData.push({
        lizeeSKU: product.Sku,
        quantity: product.quantity,
        orderToken: product.orderToken,
        returningRaisonId: returningReason.returningRaisonId,
        returningComment: returningReason.returningComment,
      })
    })
    const products = {
      products: productData,
      gender: user?.user?.gender,
      warehouseId: productToReturn[0].warehouseId,
      carrierId: productToReturn[0].carrierId,
      omsOrderId: productToReturn[0].orderNumber,
      rightOfWithdrawal: true,
    }
    const data = await postProductToReturn(products)
    if (data.type === 'POST_PRODUCT_TO_RETURN_SUCCESS') {
      if (data?.response[0]?.pdfUrl) {
        await setDownloadLink(data?.response[0]?.pdfUrl)
        await downloadURI(
          data.response[0].pdfUrl,
          `${process.env.STORE}_${data.response[0].trackingCode}`
        )
        await setIsDownloadLoading(false)
      }
    } else {
      setIsDownloadLoading(false)
    }
  }

  return (
    <>
      <div className={classes.takeBackListHeader}>
        {!activeOrder && <div className={classes.circle}></div>}
        <div className={clsx(classes.takeBackOrder)}>
          <Typography
            variant={'body1'}
            color={'primary'}
            className={classes.takeBackItemTitle}
          >
            <CMSText
              asText
              data={takeback?.data?.take_back_command}
              defaultText={t('texts:takeBack:take_back_command')}
            />
            &nbsp;
            {order.number}
          </Typography>
          <Typography variant={'body1'}>
            <strong>
              <Price amount={order.totals.total} />
            </strong>
          </Typography>
        </div>
      </div>
      <div
        className={clsx(
          classes.takeBackItem,
          lastOrder && classes.takeBackItemLast,
          activeOrder && classes.takeBackItemActive
        )}
      >
        <Typography variant={'body2'} color={'textSecondary'}>
          {orderDate}
        </Typography>
        {order.items.map((item, index) => {
          return item.units.map((unit) => {
            const isReturnable = isOrderReturnable.find(
              (isReturnableInfos) => isReturnableInfos.orderId === order.number
            )
            const isSelected = !!productToReturn.find(
              (product) => product.id === item.id
            )
            const productReturningReason =
              productReturningReasons[item.id] || {}

            return (
              <>
                <div className={classes.productCard} key={unit.puid}>
                  {activeOrder && (
                    <div
                      className={clsx(classes.productCardCheckbox, 'checkbox')}
                    >
                      <Fade in={isModifying}>
                        <Checkbox
                          checked={isSelected}
                          name={item.name}
                          data-testid={`order__checkbox--${index}`}
                          disableRipple
                          size="small"
                          disabled={!isReturnable.returnable}
                          onChange={() =>
                            handleProductSelection(
                              item.product.variants[0].sku,
                              item.id,
                              1,
                              order?.tokenValue,
                              item,
                              order.number,
                              warehouseId,
                              order.shipments[0].method.carrierId
                            )
                          }
                        />
                      </Fade>
                    </div>
                  )}
                  <div className={classes.productCardImg}>
                    <ImgKitHandler
                      classes={classes.imgProduct}
                      imagePaths={item.product.images[0]}
                      settings={{
                        width: 50,
                        ar: '1-1',
                        defaultImage: main?.data?.default_image,
                      }}
                    />
                  </div>
                  <div className={classes.productCardContent}>
                    <div className={classes.productName}>
                      {item.product.name}
                      <>&nbsp;-&nbsp;</>
                      {item.product.variants[0].size}
                    </div>
                    <div className={classes.productName}>
                      {states[`${unit.productCondition}`]?.title}
                    </div>
                    <div className={classes.productName}>
                      <Price amount={item.total} />
                    </div>
                  </div>
                </div>

                {/* Show returning reason form only when this specific product is selected */}
                {isSelected && isModifying && (
                  <ReturningReasonForm
                    productId={item.id}
                    returningRaisonId={
                      productReturningReason.returningRaisonId || null
                    }
                    returningComment={
                      productReturningReason.returningComment || ''
                    }
                    onReturningRaisonChange={handleReturningReasonChange}
                    onReturningCommentChange={
                      handleReturningReasonCommentChange
                    }
                    account={account}
                    t={t}
                  />
                )}

                <div className={classes.downloadInvoiceButton}>
                  <DownloadInvoiceButton
                    accountRes={account}
                    orderToken={order.tokenValue}
                    t={t}
                    handleDownloadInvoice={handleDownloadInvoice}
                    invoiceDownloadError={invoiceDownloadError}
                    isSub={isSubscription}
                  />
                </div>
              </>
            )
          })
        })}

        {activeOrder && (
          <Box textAlign={'center'} className={classes.returnCtaContainer}>
            {productSelection && downloadLink === '' ? (
              <Button
                data-testid={'downloadBtn'}
                variant="contained"
                color="primary"
                disabled={isDownloadLoading || !productSelection}
                onClick={() => sendProductsToReturn()}
              >
                <span
                  className={isDownloadLoading ? classes.hide : classes.show}
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_generate}
                    defaultText={t('texts:account:order_details_generate')}
                  />
                </span>
                {isDownloadLoading && <Loader />}
              </Button>
            ) : (
              downloadLink !== '' && (
                <DownloadLink
                  downloadLink={downloadLink}
                  data={account?.data?.order_details_download}
                  defaultText={t('texts:account:order_details_download')}
                />
              )
            )}
          </Box>
        )}

        {!activeOrder && (
          <div className={classes.orderDetailsRow}>
            <MainTooltip
              placement={'top'}
              title={!isDisabled ? tooltipWithdrawalText : ''}
              arrow
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => handleActiveCurrentOrder(index)}
                  className={clsx(
                    classes.orderDetailsBtn,
                    'btn--order-details'
                  )}
                  disabled={!isDisabled}
                >
                  <CMSText
                    asText
                    data={account?.data?.order_details_open}
                    defaultText={t('texts:account:order_details_open')}
                  />
                </Button>
              </span>
            </MainTooltip>
          </div>
        )}
      </div>
    </>
  )
}

export default TakeBackOrderItem
